import { styled } from 'styled-system/jsx'

const PageStack = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px 24px 80px 24px',
    maxWidth: '880px',
    width: '100%',
    marginX: 'auto',
  },
})

const Header = styled('header', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const HeaderRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
})

const HeaderText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: '0',
  },
})

const PageTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '32px',
    fontWeight: '700',
    letterSpacing: '-0.5px',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const PageSubtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.5',
  },
})

const HeaderActions = styled('div', {
  base: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
})

const PrimaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '20px',
    paddingY: '10px',
    cursor: 'pointer',
    boxShadow: '0 8px 22px -10px rgba(128, 84, 255, 0.55)',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}, opacity {durations.fast} {easings.appleStandard}',
    _hover: {
      transform: 'translateY(-1px)',
    },
    _disabled: {
      opacity: '0.55',
      cursor: 'not-allowed',
      transform: 'none',
    },
  },
})

const SecondaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '18px',
    paddingY: '10px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.primary',
      color: 'quickpass.primary',
    },
    _disabled: {
      opacity: '0.55',
      cursor: 'not-allowed',
    },
  },
})

const DangerButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.warmth',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: 'sm',
    _hover: {
      backgroundColor: 'rgba(255, 139, 115, 0.12)',
    },
  },
})

const EmptyCard = styled('div', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '40px 28px',
    boxShadow: '0 18px 50px -28px rgba(128, 84, 255, 0.22)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '12px',
  },
})

const EmptyEmoji = styled('div', {
  base: {
    fontSize: '40px',
    lineHeight: '1',
  },
})

const EmptyTitle = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '22px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const EmptyBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '460px',
  },
})

const SearchList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const SearchCard = styled('article', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '20px 22px',
    boxShadow: '0 14px 36px -22px rgba(128, 84, 255, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const SearchCardTop = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
  },
})

const SearchCardHeading = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '0',
  },
})

const SearchName = styled('h3', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.3px',
  },
})

const CriteriaLine = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.5',
  },
})

const SearchCardMeta = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const Badge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '{colors.quickpass.surfaceCool}',
    color: 'quickpass.ink',
    fontWeight: '600',
    fontSize: '11px',
    paddingX: '10px',
    paddingY: '4px',
    borderRadius: 'pill',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
})

const SearchCardActions = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
})

const RunResultBanner = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.ink',
    backgroundColor: 'rgba(127, 216, 181, 0.22)',
    border: '1px solid',
    borderColor: 'rgba(127, 216, 181, 0.7)',
    borderRadius: 'sm',
    padding: '10px 14px',
  },
})

const FormCard = styled('section', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    padding: '24px 24px 20px 24px',
    boxShadow: '0 18px 48px -28px rgba(128, 84, 255, 0.28)',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const FormHeading = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.3px',
  },
})

const FormGrid = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '14px',
  },
})

const Field = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const FieldLabel = styled('label', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
  },
})

const FieldInput = styled('input', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'sm',
    padding: '10px 12px',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      boxShadow: '0 0 0 3px rgba(128, 84, 255, 0.18)',
    },
    _placeholder: {
      color: '#9F94B8',
    },
  },
})

const FieldSelect = styled('select', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'sm',
    padding: '10px 12px',
    cursor: 'pointer',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      boxShadow: '0 0 0 3px rgba(128, 84, 255, 0.18)',
    },
  },
})

const NotifyToggleRow = styled('label', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    cursor: 'pointer',
  },
})

const FormActions = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    flexWrap: 'wrap',
  },
})

const ErrorBanner = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.warmth',
    padding: '10px 14px',
    borderRadius: 'sm',
  },
})

export {
  PageStack,
  Header,
  HeaderRow,
  HeaderText,
  PageTitle,
  PageSubtitle,
  HeaderActions,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  EmptyCard,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
  SearchList,
  SearchCard,
  SearchCardTop,
  SearchCardHeading,
  SearchName,
  CriteriaLine,
  SearchCardMeta,
  Badge,
  SearchCardActions,
  RunResultBanner,
  FormCard,
  FormHeading,
  FormGrid,
  Field,
  FieldLabel,
  FieldInput,
  FieldSelect,
  NotifyToggleRow,
  FormActions,
  ErrorBanner,
}
